<template>
  <div class="tw-p-4 sm:tw-p-6">
    <div v-if="loading">
      <p>Loading</p>
    </div>

    <div
      v-if="saveMessage"
      class="tw-mb-4 tw-rounded-xl tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-px-4 tw-py-3 tw-text-sm tw-text-emerald-900"
    >
      {{ saveMessage }}
    </div>

    <div class="tw-mb-4 tw-text-left">
      <h3 class="tw-text-lg tw-font-semibold tw-text-slate-900">Terminal Logins</h3>
      <div class="tw-mt-1 tw-text-sm tw-text-slate-600">
        <span v-if="canEditTerminalLogins">Credentials are only editable when you click the wrench.</span>
        <span v-else>Only company admins can edit terminal credentials.</span>
      </div>
    </div>

    <div class="tw-overflow-x-auto">
      <table class="tw-min-w-full tw-border-separate tw-border-spacing-0 tw-text-left">
      <thead>
        <tr class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
          <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Terminal</th>
          <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Username</th>
          <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-backdrop-blur tw-px-3 tw-py-3">Password</th>
        </tr>
      </thead>
      <tbody class="tw-divide-y tw-divide-slate-200/70">
        <tr>
          <td class="tw-px-3 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-900">ETS</td>
          <td>
            <div class="tw-flex tw-items-stretch tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-shadow-sm tw-shadow-slate-900/5 tw-overflow-hidden">
              <template v-if="canEditTerminalLogins">
                <button
                  v-if="editingTerminal !== 'ETS' || editingField !== 'username'"
                  type="button"
                  class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-600 hover:tw-bg-white"
                  @click="startEdit('ETS', 'username')"
                  title="Edit ETS"
                  aria-label="Edit ETS"
                >
                  <font-awesome-icon icon="wrench" class="tw-h-4 tw-w-4" />
                </button>
                <template v-else>
                  <button
                    type="button"
                    class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-900 hover:tw-bg-white"
                    @click="saveEdit('ETS', 'username')"
                    aria-label="Save"
                  >
                    <font-awesome-icon icon="check" class="tw-h-4 tw-w-4" />
                  </button>
                  <button
                    type="button"
                    class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-600 hover:tw-bg-white"
                    @click="cancelEdit"
                    aria-label="Cancel"
                  >
                    <font-awesome-icon icon="xmark" class="tw-h-4 tw-w-4" />
                  </button>
                </template>
              </template>

              <input
                v-model="terminalLoginEtsUsername"
                class="terminal-login-input tw-flex-1 tw-min-w-0 tw-border-0 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none"
                type="text"
                placeholder="Username"
                :readonly="!canEditTerminalLogins || editingTerminal !== 'ETS' || editingField !== 'username'"
              />
            </div>
          </td>

          <td>
            <div class="tw-flex tw-items-stretch tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-shadow-sm tw-shadow-slate-900/5 tw-overflow-hidden">
              <template v-if="canEditTerminalLogins">
                <button
                  v-if="editingTerminal !== 'ETS' || editingField !== 'password'"
                  type="button"
                  class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-600 hover:tw-bg-white"
                  @click="startEdit('ETS', 'password')"
                  title="Edit ETS"
                  aria-label="Edit ETS"
                >
                  <font-awesome-icon icon="wrench" class="tw-h-4 tw-w-4" />
                </button>
                <template v-else>
                  <button
                    type="button"
                    class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-900 hover:tw-bg-white"
                    @click="saveEdit('ETS', 'password')"
                    aria-label="Save"
                  >
                    <font-awesome-icon icon="check" class="tw-h-4 tw-w-4" />
                  </button>
                  <button
                    type="button"
                    class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-600 hover:tw-bg-white"
                    @click="cancelEdit"
                    aria-label="Cancel"
                  >
                    <font-awesome-icon icon="xmark" class="tw-h-4 tw-w-4" />
                  </button>
                </template>
              </template>

              <input
                v-model="terminalLoginEtsPassword"
                class="terminal-login-input tw-flex-1 tw-min-w-0 tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none"
                type="password"
                placeholder="Password"
                :readonly="!canEditTerminalLogins || editingTerminal !== 'ETS' || editingField !== 'password'"
              />
            </div>
          </td>
        </tr>
        <tr>
          <td class="tw-px-3 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-900">OICT/SSA</td>
          <td>
            <div class="tw-flex tw-items-stretch tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-shadow-sm tw-shadow-slate-900/5 tw-overflow-hidden">
              <template v-if="canEditTerminalLogins">
                <button
                  v-if="editingTerminal !== 'SSA' || editingField !== 'username'"
                  type="button"
                  class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-600 hover:tw-bg-white"
                  @click="startEdit('SSA', 'username')"
                  title="Edit SSA"
                  aria-label="Edit SSA"
                >
                  <font-awesome-icon icon="wrench" class="tw-h-4 tw-w-4" />
                </button>
                <template v-else>
                  <button
                    type="button"
                    class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-900 hover:tw-bg-white"
                    @click="saveEdit('SSA', 'username')"
                    aria-label="Save"
                  >
                    <font-awesome-icon icon="check" class="tw-h-4 tw-w-4" />
                  </button>
                  <button
                    type="button"
                    class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-600 hover:tw-bg-white"
                    @click="cancelEdit"
                    aria-label="Cancel"
                  >
                    <font-awesome-icon icon="xmark" class="tw-h-4 tw-w-4" />
                  </button>
                </template>
              </template>

              <input
                v-model="terminalLoginSsaUsername"
                class="terminal-login-input tw-flex-1 tw-min-w-0 tw-border-0 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none"
                type="text"
                placeholder="Username"
                :readonly="!canEditTerminalLogins || editingTerminal !== 'SSA' || editingField !== 'username'"
              />
            </div>
          </td>
          <td>
            <div class="tw-flex tw-items-stretch tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-shadow-sm tw-shadow-slate-900/5 tw-overflow-hidden">
              <template v-if="canEditTerminalLogins">
                <button
                  v-if="editingTerminal !== 'SSA' || editingField !== 'password'"
                  type="button"
                  class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-600 hover:tw-bg-white"
                  @click="startEdit('SSA', 'password')"
                  title="Edit SSA"
                  aria-label="Edit SSA"
                >
                  <font-awesome-icon icon="wrench" class="tw-h-4 tw-w-4" />
                </button>
                <template v-else>
                  <button
                    type="button"
                    class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-900 hover:tw-bg-white"
                    @click="saveEdit('SSA', 'password')"
                    aria-label="Save"
                  >
                    <font-awesome-icon icon="check" class="tw-h-4 tw-w-4" />
                  </button>
                  <button
                    type="button"
                    class="tw-inline-flex tw-items-center tw-justify-center tw-px-3 tw-text-slate-600 hover:tw-bg-white"
                    @click="cancelEdit"
                    aria-label="Cancel"
                  >
                    <font-awesome-icon icon="xmark" class="tw-h-4 tw-w-4" />
                  </button>
                </template>
              </template>

              <input
                v-model="terminalLoginSsaPassword"
                class="terminal-login-input tw-flex-1 tw-min-w-0 tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none"
                type="password"
                placeholder="Password"
                :readonly="!canEditTerminalLogins || editingTerminal !== 'SSA' || editingField !== 'password'"
              />
            </div>
          </td>
        </tr>
        <tr>
          <td class="tw-px-3 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-900">Trapac</td>
          <td colspan="2" class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">No Login needed for Trapac</td>
        </tr>
      </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { pushToast } from '@/lib/toast';
import ProfileQuery from '@/gql/user/Admin.gql';
import ProfileMutation from '@/gql/company/mutations/UpdateCompanyInfo.gql';

export default defineComponent({
  computed: {
    canEditTerminalLogins(): boolean {
      return useAuthStore().isAdmin;
    },
  },
  data() {
    return {
      terminalLoginEtsUsername: '',
      terminalLoginEtsPassword: '',
      terminalLoginSsaPassword: '',
      terminalLoginSsaUsername: '',
      editingTerminal: null as 'ETS' | 'SSA' | null,
      editingField: null as 'username' | 'password' | null,
      originalEtsUsername: '',
      originalEtsPassword: '',
      originalSsaUsername: '',
      originalSsaPassword: '',
      user: {} as any,
      loading: false,
      saveMessage: '' as string,
    };
  },
  methods: {
    startEdit(terminal: 'ETS' | 'SSA', field: 'username' | 'password') {
      const vm = this as any;
      if (!vm.canEditTerminalLogins) {
        return;
      }
      vm.editingTerminal = terminal;
      vm.editingField = field;

      vm.originalEtsUsername = vm.terminalLoginEtsUsername;
      vm.originalEtsPassword = vm.terminalLoginEtsPassword;
      vm.originalSsaUsername = vm.terminalLoginSsaUsername;
      vm.originalSsaPassword = vm.terminalLoginSsaPassword;
    },
    cancelEdit() {
      const vm = this as any;
      vm.terminalLoginEtsUsername = vm.originalEtsUsername;
      vm.terminalLoginEtsPassword = vm.originalEtsPassword;
      vm.terminalLoginSsaUsername = vm.originalSsaUsername;
      vm.terminalLoginSsaPassword = vm.originalSsaPassword;
      vm.editingTerminal = null;
      vm.editingField = null;
    },
    saveEdit(_terminal: 'ETS' | 'SSA', _field: 'username' | 'password') {
      const vm = this as any;
      vm.updateProfile();
    },
    updateProfile() {
      const vm = this as any;

      if (!vm.canEditTerminalLogins) {
        pushToast('Only company admins can edit terminal credentials', 'error');
        return;
      }

      vm.loading = true;
      vm.$apollo
        .mutate({
          mutation: ProfileMutation,

          variables: {
            companyAddress: null,
            loginEtsUsername: this.terminalLoginEtsUsername,
            loginEtsPassword: this.terminalLoginEtsPassword,
            loginSsaPassword: this.terminalLoginSsaPassword,
            loginSsaUsername: this.terminalLoginSsaUsername,
          },
        })
        .then((data) => {
          this.$emit('refreshContainers');
          console.log(data);
          this.loading = false;
          (this as any).editingTerminal = null;
          (this as any).editingField = null;
          (this as any).saveMessage = 'Updated Profile Details';
          setTimeout(() => {
            (this as any).saveMessage = '';
          }, 5000);
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;
          const message = error?.graphQLErrors?.[0]?.message || error?.message || '';
          if (message.includes('Company admin access required')) {
            pushToast('Only company admins can edit terminal credentials', 'error');
          } else {
            pushToast('Failed to update terminal logins', 'error');
          }
        });
    },
  },
  apollo: {
    company: {
      query: ProfileQuery,
      result(result) {
        const vm = this as any;

        if (!result.data.company.terminalLoginEtsUsername) {
          vm.terminalLoginEtsUsername = '';
        } else {
          vm.terminalLoginEtsUsername =
            result.data.company.terminalLoginEtsUsername;
        }
        if (!result.data.company.terminalLoginEtsPassword) {
          vm.terminalLoginEtsPassword = '';
        } else {
          vm.terminalLoginEtsPassword =
            result.data.company.terminalLoginEtsPassword;
        }
        if (!result.data.company.terminalLoginSsaPassword) {
          vm.terminalLoginSsaPassword = '';
        } else {
          vm.terminalLoginSsaPassword =
            result.data.company.terminalLoginSsaPassword;
        }
        if (!result.data.company.terminalLoginSsaUsername) {
          vm.terminalLoginSsaUsername = '';
        } else {
          vm.terminalLoginSsaUsername =
            result.data.company.terminalLoginSsaUsername;
        }

        vm.originalEtsUsername = vm.terminalLoginEtsUsername;
        vm.originalEtsPassword = vm.terminalLoginEtsPassword;
        vm.originalSsaUsername = vm.terminalLoginSsaUsername;
        vm.originalSsaPassword = vm.terminalLoginSsaPassword;

        const addr = result.data.company.address;
        if (addr) {
          (this as any).companyAddress = addr;
        }

        vm.loading = false;
      },
    },
  },
});
</script>

<style scoped>
 .terminal-login-input {
   background-color: #ffffff !important;
 }

 .terminal-login-input[readonly] {
   background-color: #ffffff !important;
 }

 .terminal-login-input:-webkit-autofill,
 .terminal-login-input:-webkit-autofill:hover,
 .terminal-login-input:-webkit-autofill:focus,
 .terminal-login-input:-webkit-autofill:active {
   -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
   box-shadow: 0 0 0 1000px #ffffff inset !important;
   -webkit-text-fill-color: #0f172a !important;
   caret-color: #0f172a !important;
 }
</style>
